/* eslint-disable @typescript-eslint/no-unused-vars */
/* ─── OneNote-paneel: secties en pagina's per project ────────────────────── */
import { useState, useEffect, useCallback } from "react";
import type { OneNoteSection, OneNotePage } from "./types";
import { I } from "./shared";

/* ─── OneNote Panel ──────────────────────────────────────────────────────── */
export function OneNotePanel({graphClient,projectNumber,color,site}:{graphClient:any;projectNumber:string;color:string;site:{hostname:string;sitePath:string}}){
  const[sections,sSec]=useState<OneNoteSection[]>([]);
  const[pages,sPg]=useState<OneNotePage[]>([]);
  const[selSec,sSelSec]=useState<OneNoteSection|null>(null);
  const[siteId,sSiteId]=useState<string|null>(null);
  const[ld,sLd]=useState(false);const[err,sErr]=useState("");

  // Haal de SharePoint site-ID op (eenmalig, reset bij site-wijziging)
  const getSiteId=useCallback(async():Promise<string|null>=>{
    try{
      const s=await graphClient.api(`/sites/${site.hostname}:${site.sitePath}`).get();
      sSiteId(s.id);
      return s.id;
    }catch(e){console.warn("Site-ID ophalen mislukt:",e);return null}
  },[graphClient,site]);

  const loadPagesForSection=useCallback(async(sec:OneNoteSection,sid:string)=>{
    sPg([]);sLd(true);
    try{
      const pg=await graphClient.api(`/sites/${sid}/onenote/sections/${sec.id}/pages`)
        .top(50).orderby("lastModifiedDateTime desc")
        .select("id,title,createdDateTime,lastModifiedDateTime,links")
        .get();
      sPg(pg.value||[]);
    }catch(e:any){sErr("Pagina's laden mislukt: "+(e.message||""))}
    sLd(false);
  },[graphClient]);

  const loadProjectSection=useCallback(async(pn:string)=>{
    sLd(true);sErr("");sSec([]);sPg([]);sSelSec(null);
    try{
      const sid=await getSiteId();
      if(!sid){sErr("SharePoint-site niet bereikbaar.");sLd(false);return;}
      // Alle secties ophalen van de SharePoint-site (inclusief notitieboeknaam)
      const resp=await graphClient.api(`/sites/${sid}/onenote/sections`)
        .top(200).select("id,displayName").get();
      const allSecs=(resp.value||[]) as any[];
      // Filter op sectienaam die begint met projectnummer
      const matched=allSecs.filter((s:any)=>s.displayName?.startsWith(pn));
      if(matched.length>0){
        const secList=matched.map((s:any)=>({id:s.id,displayName:s.displayName}));
        sSec(secList);
        sSelSec(secList[0]);
        await loadPagesForSection(secList[0],sid);
      }else{
        sErr(`Geen sectie gevonden die begint met "${pn}" in het notitieboek.`);
      }
    }catch(e:any){sErr("Fout: "+(e.message||""))}
    sLd(false);
  },[graphClient,getSiteId,loadPagesForSection]);

  useEffect(()=>{loadProjectSection(projectNumber)},[projectNumber,loadProjectSection]);

  const selectSection=async(sec:OneNoteSection)=>{
    sSelSec(sec);
    const sid=await getSiteId();
    if(sid)await loadPagesForSection(sec,sid);
  };

  return <div style={{display:"flex",flex:1,minHeight:0,flexDirection:"column"}}>
    {/* Bron-label */}
    <div style={{padding:"4px 8px",borderBottom:"1px solid #E8E8E8",fontSize:10,color:"#9AA0A6",display:"flex",alignItems:"center",gap:4}}>
      <span style={{color,fontWeight:600}}>#{projectNumber}</span>
      <span style={{marginLeft:"auto"}}>Notities - offerte aanvragen</span>
    </div>
    <div style={{display:"flex",flex:1,minHeight:0}}>
      {/* Sectie-sidebar: alleen tonen als er meerdere secties matchen */}
      {sections.length>1&&<div style={{width:140,borderRight:"1px solid #E8E8E8",overflowY:"auto",flexShrink:0}}>
        {sections.map(sec=><button key={sec.id} onClick={()=>selectSection(sec)} style={{display:"block",width:"100%",textAlign:"left",padding:"6px 8px",border:"none",background:selSec?.id===sec.id?color+"10":"transparent",color:selSec?.id===sec.id?color:"#5F6368",cursor:"pointer",fontSize:10,fontWeight:selSec?.id===sec.id?700:400,fontFamily:"Segoe UI",borderBottom:"1px solid #F5F5F5"}}>{sec.displayName}</button>)}
      </div>}
      {/* Pagina-lijst */}
      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column"}}>
        {ld&&<div style={{padding:12,color:"#9AA0A6",fontSize:11}}>Laden...</div>}
        {err&&!ld&&<div style={{padding:16,color:"#9AA0A6",fontSize:11,textAlign:"center",lineHeight:1.6}}>
          {err}<br/>
          <span style={{fontSize:10}}>Zorg dat de sectienaam in OneNote begint met "{projectNumber}"</span>
        </div>}
        {pages.map(page=>{
          // Gebruik oneNoteClientUrl voor desktop-app, anders web
          const clientUrl=page.links?.oneNoteClientUrl?.href;
          const webUrl=page.links?.oneNoteWebUrl?.href;
          const url=clientUrl||webUrl||"";
          return <a key={page.id} href={url} target="_blank" rel="noopener noreferrer"
            style={{display:"flex",flexDirection:"column",padding:"7px 10px",borderBottom:"1px solid #F5F5F5",textDecoration:"none"}}
            onMouseEnter={e=>e.currentTarget.style.background="#F5F5F5"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <I.Page/>
              <span style={{fontSize:12,fontWeight:500,color:"#1A1A1A",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{page.title||"(naamloos)"}</span>
              <span style={{opacity:.3,flexShrink:0}}><I.Ext/></span>
            </div>
            <span style={{fontSize:9,color:"#9AA0A6",marginTop:2,marginLeft:18}}>
              {new Date(page.lastModifiedDateTime).toLocaleDateString("nl-NL",{day:"numeric",month:"short",year:"numeric"})}
            </span>
          </a>;
        })}
        {!ld&&!err&&pages.length===0&&selSec&&<div style={{padding:20,textAlign:"center",color:"#9AA0A6",fontSize:12}}>Geen pagina's in deze sectie.</div>}
      </div>
    </div>
  </div>
}
